import {
	faCheck,
	faCreditCard,
	faDollarCircle,
	faHandHoldingCircleDollar,
	faPercent,
	faPiggyBank,
	faX,
	IconDefinition,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	alpha,
	Box,
	Button,
	Chip,
	Divider,
	Skeleton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material'
import { ContractSummary, ContractSummaryStatus } from '@services/contractLoading.types'
import { getTestId } from '@utils/helpers'
import { largeNumToString, numToCurrency } from '@utils/stringHelpers'

import ContractChip from '../ContractChip'
import DirectDealRejectedChip from '../DirectDealRejectedChip'
import RecommendationChip from '../RecommendationChip'

type SummaryProps = {
	summary: ContractSummary | undefined
	currency: string
	onAccept: () => void
	onReject: () => void
}

export function Summary({ summary, currency, onAccept, onReject }: SummaryProps) {
	const theme = useTheme()
	if (!summary) {
		return (
			<Stack p={2} spacing={1} data-testid="contract-details-summary-skeleton">
				<Skeleton variant="text" width="40%" height="2rem" />
				<Skeleton variant="rectangular" width="100%" height={60} />
			</Stack>
		)
	}

	return (
		<Stack>
			{/* Header */}
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				p={2}
				data-testid="contract-details-summary"
			>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Typography variant="body1" fontWeight="bold">
						{summary.termSetName}
					</Typography>
					<RecommendationChip
						recommended={summary.recommend}
						data-testid="contract-details-recommendation-chip"
					/>
					<ContractChip
						onContract={summary.onContract}
						data-testid="contract-details-contract-chip"
					/>
					{summary.requiresPriceImplementation && (
						<Chip
							label="Requires Price Impl"
							data-testid="contract-details-requires-price-impl"
							sx={{
								width: 'fit-content',
								color: 'warning.dark',
								bgcolor: (theme) => alpha(theme.palette.warning.dark, 0.08),
							}}
						/>
					)}
					{summary.directDealRejected && (
						<DirectDealRejectedChip data-testid="contract-details-direct-deal-rejected" />
					)}
				</Stack>
				<Stack direction="row" spacing={1}>
					{summary.status === ContractSummaryStatus.PENDING && (
						<>
							<Button
								variant="text"
								color="error"
								startIcon={<FontAwesomeIcon icon={faX} />}
								size="small"
								sx={{ px: 2, py: 1 }}
								onClick={onReject}
								data-testid="contract-details-reject"
							>
								Remove
							</Button>
							<Button
								variant="contained"
								color="success"
								startIcon={<FontAwesomeIcon icon={faCheck} />}
								size="small"
								sx={{ px: 2 }}
								onClick={onAccept}
								data-testid="contract-details-accept"
							>
								Accept
							</Button>
						</>
					)}
				</Stack>
			</Stack>

			<Divider />

			<Stack direction="row">
				{/* Purchased Volume */}
				<Stack spacing={1} p={2} flex={1}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Box
							sx={{
								width: 20,
								height: 20,
								borderRadius: '50%',
								bgcolor: alpha(theme.palette.secondary.dark, 0.08),
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: 'inherit',
							}}
						>
							<FontAwesomeIcon
								icon={faCreditCard}
								size="sm"
								style={{
									paddingLeft: 0.5,
									fontSize: '0.7rem',
									color: theme.palette.secondary.dark,
								}}
							/>
						</Box>
						<Typography variant="body1" color="text.primary">
							Purchased Volume
						</Typography>
					</Stack>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="caption">Annual</Typography>
						<Typography variant="h6" data-testid="contract-details-summary-annual-purchase-volume">
							{numToCurrency(summary.annualPurchaseVolume, currency, 0)}
						</Typography>
					</Stack>
					<Divider sx={{ my: 1 }} />
					<Stack>
						<Typography variant="caption">Basket</Typography>
						<Typography variant="h6" data-testid="contract-details-summary-basket-purchase-volume">
							{numToCurrency(summary.dateRangePurchaseVolume, currency, 0)}
						</Typography>
					</Stack>
				</Stack>

				<Divider orientation="vertical" flexItem />

				{/* Price Savings */}
				<SavingsSummary
					icon={faDollarCircle}
					label="Price Savings"
					annualSavings={summary.annualPriceSavings}
					basketSavings={summary.dateRangePriceSavings}
					exactSavings={summary.dateRangePriceSavingsExact}
					currency={currency}
				/>

				<Divider orientation="vertical" flexItem />

				{/* Rebate Savings */}
				<SavingsSummary
					icon={faHandHoldingCircleDollar}
					label="Rebate Savings"
					annualSavings={summary.annualRebateSavings}
					basketSavings={summary.dateRangeRebateSavings}
					exactSavings={summary.dateRangeRebateSavingsExact}
					currency={currency}
				/>

				<Divider orientation="vertical" flexItem />

				{/* Total Savings */}
				<SavingsSummary
					icon={faPiggyBank}
					label="Total Savings"
					annualSavings={summary.annualTotalSavings}
					basketSavings={summary.dateRangeTotalSavings}
					exactSavings={summary.dateRangeTotalSavingsExact}
					currency={currency}
				/>

				<Divider orientation="vertical" flexItem />

				{/* Savings % */}
				<Stack spacing={1} p={2} flex={1}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Box
							sx={{
								width: 20,
								height: 20,
								borderRadius: '50%',
								bgcolor: theme.palette.divider,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: 'inherit',
							}}
						>
							<FontAwesomeIcon icon={faPercent} size="sm" />
						</Box>
						<Typography variant="body1" color="text.primary">
							Savings
						</Typography>
					</Stack>
					<Box
						width="100%"
						height="100%"
						display="flex"
						justifyContent="space-between"
						alignItems="center"
					>
						<Typography variant="h2" color="text.primary">
							{`${
								Math.abs(summary.savingsPercentage) > 999.99
									? largeNumToString(summary.savingsPercentage)
									: summary.savingsPercentage.toFixed(2)
							}%`}
						</Typography>
					</Box>
				</Stack>
			</Stack>
		</Stack>
	)
}

interface SavingsSummaryProps {
	icon: IconDefinition
	label: string
	annualSavings: number
	exactSavings: number
	basketSavings: number
	currency: string
}

function SavingsSummary({
	icon,
	label,
	annualSavings,
	exactSavings,
	basketSavings,
	currency,
	...rest
}: SavingsSummaryProps) {
	const theme = useTheme()
	const testId = getTestId(rest, `contract-details-${label.toLowerCase().replace(/\s/g, '-')}`)
	const statusColor = annualSavings >= 0 ? theme.palette.success.main : theme.palette.error.main
	const iconColor = annualSavings >= 0 ? theme.palette.success.dark : theme.palette.error.dark

	return (
		<Stack spacing={1} p={2} flex={1} data-testid={testId}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Box
					sx={{
						width: 20,
						height: 20,
						borderRadius: '50%',
						bgcolor: alpha(statusColor, 0.08),
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: 'inherit',
					}}
				>
					<FontAwesomeIcon
						icon={icon}
						style={{
							color: iconColor,
						}}
					/>
				</Box>
				<Typography variant="body1" color="text.primary">
					{label}
				</Typography>
			</Stack>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography variant="caption">Annual</Typography>
				<Typography variant="h6" sx={{ color: statusColor }} data-testid={`${testId}-annual`}>
					{numToCurrency(annualSavings, currency, 0)}
				</Typography>
			</Stack>
			<Divider sx={{ my: 1 }} />
			<Stack direction="row" spacing={5}>
				<Stack>
					<Typography variant="caption">Basket</Typography>
					<Typography variant="h6" data-testid={`${testId}-basket`}>
						{numToCurrency(basketSavings, currency, 0)}
					</Typography>
				</Stack>
				<Stack>
					<Typography variant="caption" color="text.disabled">
						Exact
					</Typography>
					<Typography variant="h6" color="text.disabled" data-testid={`${testId}-exact`}>
						{numToCurrency(exactSavings, currency, 0)}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	)
}
